import { useEffect, useState } from 'react';

import { IQuestion } from '@quizzach/interfaces';
import { consoleDev, ConsoleStatus, fetchData } from '@quizzach/utils';

import { MOCK_QUESTION_PREVIEW, MOCK_QUIZ_PLAY_VARIANT } from './mocks.config';
import { generateMockQuiz } from './quizPlay.mock';

/**
 * Prev/next existing question ids around the previewed one (deletions
 * leave gaps in the id sequence) plus its position in the full set —
 * lets the admin preview page through the entire question database.
 */
export interface IQuestionNeighbors {
	prev: number | null;
	next: number | null;
	position: number;
	total: number;
}

export const usePreviewQuestion = (id: string | undefined) => {
	const [question, setQuestion] = useState<IQuestion>();
	const [neighbors, setNeighbors] = useState<IQuestionNeighbors>();

	const getQuestion = async () => {
		if (MOCK_QUESTION_PREVIEW) {
			const mockQuestions = generateMockQuiz(
				'mock-preview',
				MOCK_QUIZ_PLAY_VARIANT
			).questions;
			const index = Math.min(
				Math.max(Number(id) || 1, 1),
				mockQuestions.length
			);
			setQuestion(mockQuestions[index - 1]);
			setNeighbors({
				prev: index > 1 ? index - 1 : null,
				next: index < mockQuestions.length ? index + 1 : null,
				position: index,
				total: mockQuestions.length,
			});
			return;
		}
		try {
			const response = await fetchData<Partial<IQuestion>>(
				`/api/questions/${id}`,
				{
					method: 'GET',
					headers: {
						Cookie: 'bslwqk3wos6vj23alfmcs=bslwqk3wos6vj23alfmcs',
					},
				},
				{
					authRoute: true,
				}
			);
			if (response.status >= 300) {
				consoleDev('getQuestion', ConsoleStatus.HTTP, response.status);
			} else {
				consoleDev('getQuestion', ConsoleStatus.SUCC);
				if (response.data) {
					const question = new IQuestion(response.data);
					setQuestion(question);
				}
			}
		} catch (err) {
			consoleDev('getQuestion', ConsoleStatus.NET, err);
		}
	};

	const getNeighbors = async () => {
		if (MOCK_QUESTION_PREVIEW) return;
		try {
			const response = await fetchData<IQuestionNeighbors>(
				`/api/questions/${id}/neighbors`,
				{ method: 'GET' },
				{ authRoute: true }
			);
			if (response.status >= 300) {
				consoleDev('getQuestionNeighbors', ConsoleStatus.HTTP, response.status);
			} else {
				consoleDev('getQuestionNeighbors', ConsoleStatus.SUCC);
				if (response.data) setNeighbors(response.data);
			}
		} catch (err) {
			consoleDev('getQuestionNeighbors', ConsoleStatus.NET, err);
		}
	};

	useEffect(() => {
		getQuestion();
		getNeighbors();
	}, [id]);

	return { question, neighbors, getQuestion };
};
